// src/components/AddTripModal.jsx
import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Autocomplete as MuiAutocomplete,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Button,
  Chip,
  Alert,
  Paper,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PlaceIcon from "@mui/icons-material/Place";
import RouteIcon from "@mui/icons-material/Route";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StraightenIcon from "@mui/icons-material/Straighten";
import TimerIcon from "@mui/icons-material/Timer";

import {
  GoogleMap,
  Autocomplete,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader,
} from "@react-google-maps/api";

import { useGetAllAssetsQuery, useGetAllLiteDevicesQuery, useGetAllUsersQuery } from "../../redux/admin/adminApi";
import { useGetMaterialsQuery, useGetClientsQuery } from "../../redux/logistics/logisticsApi";
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES } from "../../config/googleMaps";

const mapContainerStyle = {
  width: "100%",
  height: "360px",
  borderRadius: "16px",
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 };

const safeStr = (v) => (v == null ? "" : String(v));
const VEHICLE_AUTOCOMPLETE_LISTBOX_SX = {
  maxHeight: 320,
  overflowY: "auto",
  "& .MuiAutocomplete-option": {
    alignItems: "flex-start",
    py: 1,
  },
};

export default function AddTripModal({ onClose, onSubmit, open = true }) {
  const theme = useTheme();

  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES, // keep your config
  });

  // ✅ FETCH USERS & ASSETS
  const { data: users = [], isLoading: isUsersLoading } = useGetAllUsersQuery();
  const { data: assetResponse, isLoading: isVehicleLoading } = useGetAllAssetsQuery();
  const { data: devicesResponse = [] } = useGetAllLiteDevicesQuery();
  const { data: materials = [], isLoading: isMaterialsLoading } = useGetMaterialsQuery();
  const { data: clientsData = [], isLoading: isClientsLoading } = useGetClientsQuery();

  const clients = Array.isArray(clientsData) ? clientsData : [];
  const drivers = Array.isArray(users) ? users.filter((u) => u.role === "viewer") : [];

  const vehicles = Array.isArray(assetResponse)
    ? assetResponse
    : assetResponse?.assets || assetResponse?.data || [];

  const devices = useMemo(() => {
    if (Array.isArray(devicesResponse)) return devicesResponse;
    if (Array.isArray(devicesResponse?.data)) return devicesResponse.data;
    if (Array.isArray(devicesResponse?.devices)) return devicesResponse.devices;
    return [];
  }, [devicesResponse]);

  const selectableVehicles = useMemo(() => {
    const normalizedDevices = (devices || [])
      .filter((d) => d && String(d.status || "").toLowerCase() !== "deleted")
      .map((d) => ({
        _id: d._id,
        IMEI: d.imei || d.IMEI || "",
        model: d.model || "",
        name: d.name || "",
        source: "device",
      }));

    // Build IMEI → device _id map for fast lookup
    const imeiToDeviceId = new Map(
      normalizedDevices.map((d) => [String(d.IMEI).toLowerCase(), d._id])
    );

    const normalizedAssets = (vehicles || [])
      .filter((v) => v && String(v.status || "").toLowerCase() !== "deleted")
      .map((v) => {
        const assetImei = String(v.IMEI || v.imei || "").toLowerCase();
        // If a registered Device has the same IMEI, use device _id so backend
        // never hits the "not mapped" error.
        const resolvedId = imeiToDeviceId.get(assetImei) || v._id;
        return {
          _id: resolvedId,
          IMEI: v.IMEI || v.imei || "",
          model: v.model || "",
          name: v.name || "",
          source: imeiToDeviceId.has(assetImei) ? "device" : "asset",
        };
      });

    // Merge: assets first (with resolved IDs), then any devices not already listed
    const assetIds = new Set(normalizedAssets.map((v) => String(v._id)));
    const extraDevices = normalizedDevices.filter((d) => !assetIds.has(String(d._id)));
    const merged = [...normalizedAssets, ...extraDevices];

    const seen = new Set();
    return merged.filter((v) => {
      const key = String(v._id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [vehicles, devices]);

  const [formData, setFormData] = useState({
    client: "",
    vehicle: "",
    driver: "",
    material: "",
    load: "",
    startAddr: "",
    endAddr: "",
    startDate: "",
    endDate: "",
    routeName: "",
    distance: "",
    duration: "",
  });

  const [errors, setErrors] = useState({});

  const [directionsResponse, setDirectionsResponse] = useState(null);
  // Guard against the @react-google-maps/api infinite-loop: once the route is
  // fetched successfully, stop rendering <DirectionsService> so it never fires
  // the same request again on every re-render.
  const [routeFetched, setRouteFetched] = useState(false);

  const originRef = useRef(null);
  const destRef = useRef(null);

  const clearFieldError = (name) => {
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleChange = (name) => (e) => {
    const value = e?.target?.value;
    setFormData((p) => ({ ...p, [name]: value }));
    clearFieldError(name);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.client) newErrors.client = "Client is required";
    if (!formData.vehicle) newErrors.vehicle = "Vehicle is required";
    if (!formData.driver) newErrors.driver = "Driver is required";
    if (!formData.routeName) newErrors.routeName = "Route name is required";
    if (!formData.startAddr) newErrors.startAddr = "Start address is required";
    if (!formData.endAddr) newErrors.endAddr = "End address is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const directionsCallback = useCallback((response) => {
    if (response && response.status === "OK") {
      // Mark as fetched FIRST — stops <DirectionsService> from re-mounting
      // on the next render and firing another API call (infinite-loop fix).
      setRouteFetched(true);
      setDirectionsResponse(response);

      const route = response.routes?.[0]?.legs?.[0];
      if (!route) return;

      setFormData((prev) => ({
        ...prev,
        distance: route.distance?.text || "",
        duration: route.duration?.text || "",
        startAddr: route.start_address || prev.startAddr,
        endAddr: route.end_address || prev.endAddr,
      }));
    }
  }, []);

  const onPlaceSelected = () => {
    // Reset so a new route is fetched when both places are selected again
    setDirectionsResponse(null);
    setRouteFetched(false);
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const originLoc = originRef.current?.getPlace?.()?.geometry?.location;
    const destLoc = destRef.current?.getPlace?.()?.geometry?.location;

    const payload = {
      ...formData,
      startPoint:
        originLoc && typeof originLoc.lat === "function" && typeof originLoc.lng === "function"
          ? { lat: originLoc.lat(), lng: originLoc.lng() }
          : undefined,
      endPoint:
        destLoc && typeof destLoc.lat === "function" && typeof destLoc.lng === "function"
          ? { lat: destLoc.lat(), lng: destLoc.lng() }
          : undefined,
    };
    onSubmit?.(payload);
  };

  // ✅ get place geometry safely
  const originPlace = originRef.current?.getPlace?.();
  const destPlace = destRef.current?.getPlace?.();
  const canRoute =
    originPlace?.geometry?.location && destPlace?.geometry?.location;

  return (
    <Dialog
      open={open}
      onClose={() => { setRouteFetched(false); setDirectionsResponse(null); onClose?.(); }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: `0 18px 60px ${alpha(theme.palette.common.black, 0.18)}`,
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          px: 2.2,
          py: 1.6,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack spacing={0.2}>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Add Trip</Typography>
            <Typography variant="body2" color="text.secondary">
              Create a trip with route preview and auto distance/duration.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={isLoaded ? "Map ready" : "Loading map…"}
              variant="outlined"
              sx={{ fontWeight: 900, borderRadius: 999 }}
            />
            <IconButton onClick={onClose} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.04) }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ p: 0 }}>
        {!isLoaded ? (
          <Box sx={{ p: 2.5 }}>
            <Alert severity="info">Loading map…</Alert>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 2,
            }}
          >
            {/* errors banner */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">
                Please fill all required fields marked with *
              </Alert>
            )}

            {/* TOP FORM AREA */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "stretch",
                flexDirection: { xs: "column", lg: "row" },
              }}
            >
              {/* LEFT: FORM */}
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 3,
                  p: 2,
                  borderColor: alpha(theme.palette.divider, 0.9),
                }}
              >
                {/* Section: Primary */}
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 900 }}>Trip Details</Typography>
                    <Chip size="small" label="Required *" variant="outlined" sx={{ fontWeight: 900 }} />
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      select
                      label="Client Name *"
                      value={formData.client}
                      onChange={handleChange("client")}
                      error={!!errors.client}
                      helperText={errors.client || " "}
                      size="small"
                      sx={{ flex: "1 1 260px" }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                            <BusinessIcon fontSize="small" />
                          </Box>
                        ),
                      }}
                    >
                      <MenuItem value="">-- Select Client --</MenuItem>
                      {isClientsLoading ? (
                        <MenuItem disabled>Loading clients…</MenuItem>
                      ) : clients.length === 0 ? (
                        <MenuItem disabled>No clients available</MenuItem>
                      ) : (
                        clients.map((c) => (
                          <MenuItem key={c._id} value={c._id}>
                            {c.clientName} ({c.contactPersonPhone})
                          </MenuItem>
                        ))
                      )}
                    </TextField>

                    <MuiAutocomplete
                      options={selectableVehicles}
                      loading={isVehicleLoading}
                      value={selectableVehicles.find((v) => String(v._id) === String(formData.vehicle)) || null}
                      onChange={(_, value) => {
                        setFormData((prev) => ({ ...prev, vehicle: value?._id || "" }));
                        clearFieldError("vehicle");
                      }}
                      getOptionLabel={(option) =>
                        `${option?.name || option?.model || "Vehicle"}${option?.IMEI ? ` (${option.IMEI})` : ""}`
                      }
                      isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
                      filterOptions={(options, state) => {
                        const query = String(state.inputValue || "").trim().toLowerCase();
                        if (!query) return options;
                        return options.filter((option) => {
                          const haystack = [
                            option?.name,
                            option?.model,
                            option?.IMEI,
                          ]
                            .filter(Boolean)
                            .join(" ")
                            .toLowerCase();
                          return haystack.includes(query);
                        });
                      }}
                      noOptionsText={isVehicleLoading ? "Loading vehicles…" : "No vehicles available"}
                      ListboxProps={{ sx: VEHICLE_AUTOCOMPLETE_LISTBOX_SX }}
                      sx={{ flex: "1 1 260px" }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Stack spacing={0.15}>
                            <Typography sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
                              {option?.name || option?.model || "Vehicle"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                              {option?.IMEI || "No IMEI"}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vehicle *"
                          error={!!errors.vehicle}
                          helperText={errors.vehicle || " "}
                          size="small"
                          placeholder="Search vehicle"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                                  <LocalShippingIcon fontSize="small" />
                                </Box>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    <TextField
                      select
                      label="Driver *"
                      value={formData.driver}
                      onChange={handleChange("driver")}
                      error={!!errors.driver}
                      helperText={errors.driver || " "}
                      size="small"
                      sx={{ flex: "1 1 260px" }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                            <PersonIcon fontSize="small" />
                          </Box>
                        ),
                      }}
                    >
                      <MenuItem value="">-- Select Driver --</MenuItem>
                      {isUsersLoading ? (
                        <MenuItem disabled>Loading drivers…</MenuItem>
                      ) : drivers.length === 0 ? (
                        <MenuItem disabled>No drivers available</MenuItem>
                      ) : (
                        drivers.map((d) => (
                          <MenuItem key={d._id} value={d._id}>
                            {d.username} ({d.mobileNo || d.email})
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Box>

                  <Divider />

                  {/* Section: Load & Materials */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      select
                      label="Material"
                      value={formData.material}
                      onChange={handleChange("material")}
                      size="small"
                      sx={{ flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                            <Inventory2Icon fontSize="small" />
                          </Box>
                        ),
                      }}
                    >
                      <MenuItem value="">-- Select Material --</MenuItem>
                      {isMaterialsLoading ? (
                        <MenuItem disabled>Loading materials…</MenuItem>
                      ) : (materials || []).length === 0 ? (
                        <MenuItem disabled>No materials available</MenuItem>
                      ) : (
                        materials.map((m) => (
                          <MenuItem key={m._id} value={m._id}>
                            {m.name} ({m.category})
                          </MenuItem>
                        ))
                      )}
                    </TextField>

                    <TextField
                      label="Load (tons)"
                      type="number"
                      value={formData.load}
                      onChange={handleChange("load")}
                      size="small"
                      sx={{ width: { xs: "100%", md: 220 } }}
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </Stack>

                  {/* Dates */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      label="Start Date & Time *"
                      type="datetime-local"
                      value={formData.startDate}
                      onChange={handleChange("startDate")}
                      error={!!errors.startDate}
                      helperText={errors.startDate || " "}
                      size="small"
                      sx={{ flex: 1 }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                            <CalendarMonthIcon fontSize="small" />
                          </Box>
                        ),
                      }}
                    />

                    <TextField
                      label="End Date & Time (Optional)"
                      type="datetime-local"
                      value={formData.endDate}
                      onChange={handleChange("endDate")}
                      size="small"
                      sx={{ flex: 1 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  {/* Route name */}
                  <TextField
                    label="Route Name *"
                    value={formData.routeName}
                    onChange={handleChange("routeName")}
                    error={!!errors.routeName}
                    helperText={errors.routeName || " "}
                    size="small"
                    placeholder="e.g., Delhi to Mumbai via NH48"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                          <RouteIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                  />

                  {/* Distance/Duration (readonly) */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.2,
                      borderRadius: 2.5,
                      borderColor: alpha(theme.palette.divider, 0.9),
                      bgcolor: alpha(theme.palette.text.primary, 0.02),
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StraightenIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Route Length
                        </Typography>
                      </Stack>

                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {formData.distance ? safeStr(formData.distance) : "—"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 0.6 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TimerIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Duration
                        </Typography>
                      </Stack>

                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {formData.duration ? safeStr(formData.duration) : "—"}
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Paper>

              {/* RIGHT: Addresses */}
              <Paper
                variant="outlined"
                sx={{
                  width: { xs: "100%", lg: 420 },
                  flexShrink: 0,
                  borderRadius: 3,
                  p: 2,
                  borderColor: alpha(theme.palette.divider, 0.9),
                }}
              >
                <Stack spacing={1.2}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 900 }}>Route Addresses</Typography>
                    <Chip
                      size="small"
                      label={canRoute ? "Ready to route" : "Pick start & end"}
                      variant="outlined"
                      sx={{ fontWeight: 900 }}
                    />
                  </Stack>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                      Start Address *
                    </Typography>
                    <Autocomplete
                      onLoad={(ref) => (originRef.current = ref)}
                      onPlaceChanged={onPlaceSelected}
                    >
                      <TextField
                        value={formData.startAddr}
                        onChange={handleChange("startAddr")}
                        error={!!errors.startAddr}
                        helperText={errors.startAddr || " "}
                        size="small"
                        placeholder="Enter start location"
                        InputProps={{
                          startAdornment: (
                            <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                              <PlaceIcon fontSize="small" />
                            </Box>
                          ),
                        }}
                      />
                    </Autocomplete>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                      End Address *
                    </Typography>
                    <Autocomplete
                      onLoad={(ref) => (destRef.current = ref)}
                      onPlaceChanged={onPlaceSelected}
                    >
                      <TextField
                        value={formData.endAddr}
                        onChange={handleChange("endAddr")}
                        error={!!errors.endAddr}
                        helperText={errors.endAddr || " "}
                        size="small"
                        placeholder="Enter destination"
                        InputProps={{
                          startAdornment: (
                            <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "text.secondary" }}>
                              <PlaceIcon fontSize="small" />
                            </Box>
                          ),
                        }}
                      />
                    </Autocomplete>
                  </Box>

                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2.5,
                      "& .MuiAlert-message": { width: "100%" },
                    }}
                  >
                    Select start + end and the route will auto-calculate.
                  </Alert>
                </Stack>
              </Paper>
            </Box>

            {/* MAP */}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                borderColor: alpha(theme.palette.divider, 0.9),
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontWeight: 900 }}>Map Preview</Typography>
                  <Chip
                    size="small"
                    label={
                      formData.distance && formData.duration
                        ? `${formData.distance} • ${formData.duration}`
                        : "No route yet"
                    }
                    variant="outlined"
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                  Zoom / drag to inspect
                </Typography>
              </Box>

              <Box sx={{ p: 1.5 }}>
                <GoogleMap mapContainerStyle={mapContainerStyle} center={defaultCenter} zoom={5}>
                  {canRoute && !routeFetched && (
                    <DirectionsService
                      options={{
                        origin: originPlace.geometry.location,
                        destination: destPlace.geometry.location,
                        travelMode: "DRIVING",
                      }}
                      callback={directionsCallback}
                    />
                  )}

                  {directionsResponse && <DirectionsRenderer directions={directionsResponse} />}
                </GoogleMap>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 2,
          py: 1.6,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.95),
        }}
      >
        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, alignSelf: "center" }}>
            * Required fields
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
            >
              Submit Trip
            </Button>
          </Stack>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
